import React, { useRef, useEffect } from "react";
import { CheckCircle2, Lock, PlayCircle, Circle, GitBranch } from "lucide-react";

const STATO_STYLE = {
  completato: {
    dot: "bg-emerald-500 text-white ring-emerald-200",
    line: "bg-emerald-500",
    label: "text-emerald-700",
    Icon: CheckCircle2,
  },
  in_corso: {
    dot: "bg-[#f59e0b] text-white ring-amber-200",
    line: "bg-slate-200",
    label: "text-[#b45309]",
    Icon: PlayCircle,
  },
  da_iniziare: {
    dot: "bg-white text-[#102a6b] ring-[#102a6b]/30 border-2 border-[#102a6b]",
    line: "bg-slate-200",
    label: "text-[#102a6b]",
    Icon: Circle,
  },
  bloccato: {
    dot: "bg-slate-100 text-slate-400 ring-slate-200",
    line: "bg-slate-200",
    label: "text-slate-500",
    Icon: Lock,
  },
};

/**
 * Timeline orizzontale delle tappe del corso con stato di completamento.
 * Props:
 *  - moduli: array di { id, numero_label, titolo, stato, gruppo }
 *  - onOpen: (modulo) => void — cliccabile se non bloccato
 */
export default function TimelineFormazione({ moduli = [], onOpen }) {
  const scrollRef = useRef(null);

  // Auto-scroll al primo modulo non completato
  useEffect(() => {
    if (!scrollRef.current || moduli.length === 0) return;
    const idx = moduli.findIndex((m) => m.stato !== "completato");
    if (idx > 2) {
      const target = scrollRef.current.querySelector(`[data-idx="${idx}"]`);
      if (target) {
        const cont = scrollRef.current;
        cont.scrollTo({
          left: target.offsetLeft - cont.clientWidth / 2 + target.clientWidth / 2,
          behavior: "smooth",
        });
      }
    }
  }, [moduli]);

  if (moduli.length === 0) return null;

  const totali = moduli.length;
  const completati = moduli.filter((m) => m.stato === "completato").length;

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6"
      data-testid="timeline-formazione"
    >
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <GitBranch className="h-5 w-5 text-[#102a6b] shrink-0" />
          <h3 className="font-extrabold text-[#0a1f55] text-[15px] md:text-[16px] truncate">
            Timeline del percorso formativo
          </h3>
        </div>
        <div className="text-[12px] font-extrabold text-[#0a1f55] whitespace-nowrap">
          <span className="text-emerald-600">{completati}</span>
          <span className="text-slate-400"> / </span>
          <span>{totali}</span>
          <span className="text-slate-500 ml-1 font-semibold">completati</span>
        </div>
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap gap-3 mb-4 text-[11px] font-semibold">
        <LegendItem color="bg-emerald-500" label="Completato" />
        <LegendItem color="bg-[#f59e0b]" label="In corso" />
        <LegendItem
          color="bg-white border-2 border-[#102a6b]"
          label="Da iniziare"
        />
        <LegendItem color="bg-slate-200" label="Bloccato" />
      </div>

      {/* Timeline scrollabile */}
      <div
        ref={scrollRef}
        className="overflow-x-auto pb-3 -mx-1 px-1"
        style={{ scrollbarWidth: "thin" }}
      >
        <div className="relative flex items-start min-w-max pt-2">
          {moduli.map((m, i) => {
            const st = STATO_STYLE[m.stato] || STATO_STYLE.da_iniziare;
            const isLocked = m.stato === "bloccato";
            const nextSt = i < moduli.length - 1
              ? (STATO_STYLE[moduli[i + 1].stato] || STATO_STYLE.da_iniziare)
              : null;
            // La linea è verde solo se ENTRAMBI i nodi ai capi sono completati
            const lineCls =
              m.stato === "completato" && moduli[i + 1]?.stato === "completato"
                ? "bg-emerald-500"
                : "bg-slate-200";
            return (
              <div
                key={m.id}
                data-idx={i}
                data-testid={`timeline-node-${m.id}`}
                className="flex flex-col items-center relative"
                style={{ minWidth: 96 }}
              >
                {/* Linea a destra (tranne l'ultimo) */}
                {i < moduli.length - 1 && (
                  <div
                    className={`absolute top-4 left-1/2 h-1 ${lineCls} transition-colors`}
                    style={{ width: "100%" }}
                  />
                )}
                {/* Nodo */}
                <button
                  type="button"
                  onClick={() => !isLocked && onOpen && onOpen(m)}
                  disabled={isLocked}
                  aria-label={`${m.numero_label} — ${m.stato}`}
                  className={`relative z-10 h-9 w-9 rounded-full ring-4 flex items-center justify-center transition-all ${
                    st.dot
                  } ${
                    isLocked
                      ? "cursor-not-allowed"
                      : "cursor-pointer hover:scale-110 hover:ring-[6px]"
                  }`}
                >
                  <st.Icon className="h-4 w-4" />
                </button>
                {/* Etichetta sotto */}
                <div className="mt-2 text-center px-1" style={{ maxWidth: 96 }}>
                  <div
                    className={`text-[10px] font-extrabold uppercase tracking-wider truncate ${st.label}`}
                    title={m.numero_label}
                  >
                    {m.numero_label.replace(/^Modulo\s+/i, "M.")}
                  </div>
                  <div
                    className="text-[10px] text-slate-500 leading-tight line-clamp-2 mt-0.5"
                    title={m.titolo}
                  >
                    {m.titolo}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-600">
      <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
      {label}
    </span>
  );
}
